import { macroAnalysisMessageSchema, generateMacroAnalysisMessage, MacroAnalysisMessageType, analysisProgressSchema,
  type AnalysisCommand, type AnalysisReply, type Lease, type MacroAnalysisMessage } from '@playcastdotio/Messaging';
import type { INativeMacroAnalysisClient, AnalysisClientLogger } from '../../services/playjector/macroAnalysis/INativeMacroAnalysisClient';
import type { IRealtimeService } from '../../services/realtime/IRealtimeService';
import { AnalysisOutbox } from './AnalysisOutbox';

export interface AnalysisCoordinatorClock { now(): number; every(milliseconds: number, callback: () => void): () => void }
type AssignmentCommand = Extract<AnalysisCommand, { command: 'analysisStart' | 'analysisPreview' }>;
interface ActiveAnalysis { lease: Lease; requestId: string; deadline: number; heartbeatAt: number; lastSequence: number; outbox: AnalysisOutbox; kind: 'calibration' | 'preview' }

/** Owns a single native analysis lease; game launch and input services are deliberately absent. */
export class MacroAnalysisCoordinator {
  private active?: ActiveAnalysis;
  private authenticated = false;
  private probing = false;
  private probeAt = -Infinity;
  private disposers: (() => void)[] = [];
  private commandQueue: Promise<void> = Promise.resolve();
  private readonly epochs = new Map<string, number>();
  private queuedPreview?: AssignmentCommand;
  public constructor(private readonly native: INativeMacroAnalysisClient, private readonly realtime: IRealtimeService,
    private readonly clock: AnalysisCoordinatorClock, private readonly logger: AnalysisClientLogger) {}
  public start(): void {
    if (this.disposers.length) return;
    // Coordinators load asynchronously. AUTH_SUCCESS may already have made the shared
    // connection ready before this listener is registered, so seed authentication from
    // the authoritative readiness state instead of waiting for a future reconnect.
    this.authenticated = this.realtime.isConnected();
    this.disposers = [this.native.subscribe(reply => this.onNative(reply)),
      this.realtime.onConnected(() => {
        this.authenticated = this.realtime.isConnected();
        this.queuedPreview = undefined;
        this.stopActive('analysis_connection_replaced', false);
        if (this.authenticated) { this.probeAt = -Infinity; void this.tick(); }
      }),
      this.realtime.onDisconnected(() => {
        this.authenticated = false;
        this.queuedPreview = undefined;
        this.stopActive('analysis_connection_lost', false);
      }),
      this.realtime.onHostAuthSuccess(() => { this.authenticated = true; this.probeAt = -Infinity; void this.tick(); }),
      this.clock.every(1000, () => { void this.tick(); })];
    if (this.authenticated) { this.probeAt = -Infinity; void this.tick(); }
  }
  public dispose(): void {
    this.disposers.forEach(dispose => dispose()); this.disposers = []; this.authenticated = false;
    this.queuedPreview = undefined;
    this.stopActive('analysis_coordinator_disposed', false);
  }
  public handle(input: unknown): Promise<void> {
    const parsed = macroAnalysisMessageSchema.safeParse(input);
    if (!parsed.success) {
      this.log('analysis_message_rejected', { reason: 'invalid_schema' });
      return Promise.resolve();
    }
    if (!this.authenticated) {
      this.log('analysis_message_rejected', { reason: 'unauthenticated', type: parsed.data.type });
      return Promise.resolve();
    }
    this.commandQueue = this.commandQueue.then(() => this.command(parsed.data)).catch(() => this.fail('analysis_native_command_failed'));
    return this.commandQueue;
  }
  public async tick(): Promise<void> {
    if (!this.authenticated) return;
    if (!this.realtime.isConnected()) { this.authenticated = false; this.queuedPreview = undefined; this.stopActive('analysis_connection_lost', false); return; }
    const now = this.clock.now(), active = this.active;
    if (active && now >= active.deadline) this.stopActive('analysis_lease_expired', true);
    else if (active) {
      if (now - active.heartbeatAt >= 5000) {
        active.heartbeatAt = now;
        this.send(generateMacroAnalysisMessage({ type: MacroAnalysisMessageType.HEARTBEAT, requestId: active.requestId, lease: active.lease }));
      }
      this.flush(active);
    }
    if (!this.probing && now - this.probeAt >= 20_000) await this.advertise();
  }
  private async command(message: MacroAnalysisMessage): Promise<void> {
    if (!this.authenticated || !this.realtime.isConnected()) return;
    if (message.type === MacroAnalysisMessageType.CAPABILITIES) { await this.advertise(); return; }
    if (message.type === MacroAnalysisMessageType.ASSIGN) {
      const command = message.command;
      if (command.command !== 'analysisStart' && command.command !== 'analysisPreview') return;
      if (command.command === 'analysisPreview' && this.active?.kind === 'calibration') {
        this.queuedPreview = command;
        this.publishSurface();
        this.log('analysis_preview_queued', { jobId: command.lease.jobId, epoch: command.lease.epoch });
        return;
      }
      await this.acceptAssignment(command);
      return;
    }
    const active = this.active;
    const lease = 'command' in message ? message.command.lease : 'lease' in message ? message.lease : 'reply' in message ? message.reply.lease : null;
    if (!active || !lease || !this.sameAttempt(active.lease, lease)) return;
    if (message.type === MacroAnalysisMessageType.RENEW) {
      if (lease.renewalSequence <= active.lease.renewalSequence || this.clock.now() >= active.deadline) return;
      active.lease = lease; active.deadline = this.clock.now() + lease.remainingMs;
      await this.native.send(message.command);
    } else if (message.type === MacroAnalysisMessageType.CANCEL) this.stopActive('analysis_server_cancelled', true);
    else if (message.type === MacroAnalysisMessageType.ACK) { active.outbox.acknowledge(message.receipt); this.flush(active); }
    else if (message.type === MacroAnalysisMessageType.PROGRESS && ['completed', 'failed', 'cancelled', 'superseded'].includes(message.reply.status)) {
      this.stopActive('analysis_server_' + message.reply.status, true);
    }
  }
  private async acceptAssignment(command: AssignmentCommand): Promise<void> {
    const previous = this.active;
    if (previous && this.sameAttempt(previous.lease, command.lease)) {
      this.log('analysis_assignment_rejected', { jobId: command.lease.jobId, epoch: command.lease.epoch, reason: 'duplicate_attempt' });
      return;
    }
    if ((this.epochs.get(command.lease.jobId) ?? -1) >= command.lease.epoch) {
      this.log('analysis_assignment_rejected', { jobId: command.lease.jobId, epoch: command.lease.epoch, reason: 'stale_epoch' });
      return;
    }
    this.log('analysis_assignment_received', { jobId: command.lease.jobId, epoch: command.lease.epoch,
      hostId: command.lease.hostId, requestId: command.requestId, kind: command.command === 'analysisPreview' ? 'preview' : 'calibration' });
    this.epochs.set(command.lease.jobId, command.lease.epoch);
    if (this.epochs.size > 64) this.epochs.delete(this.epochs.keys().next().value!);
    this.stopActive('analysis_lease_replaced', false);
    const kind = command.command === 'analysisPreview' ? 'preview' : 'calibration';
    this.active = { lease: command.lease, requestId: command.requestId, deadline: this.clock.now() + command.lease.remainingMs,
      heartbeatAt: this.clock.now(), lastSequence: -1, kind, outbox: new AnalysisOutbox(command.command === 'analysisStart' ? command.acknowledged : []) };
    this.publishSurface();
    await this.native.send(command);
    this.log('analysis_host_assigned', { jobId: command.lease.jobId, epoch: command.lease.epoch, kind });
  }
  private onNative(reply: AnalysisReply): void {
    if (reply.status === 'capabilities') return;
    const active = this.active;
    if (!active || !this.authenticated || !this.sameAttempt(active.lease, reply.lease) || reply.requestId !== active.requestId) return;
    if (this.clock.now() >= active.deadline) { this.stopActive('analysis_lease_expired', true); return; }
    if (reply.sequence <= active.lastSequence) return;
    active.lastSequence = reply.sequence;
    try {
      if (reply.chunk || reply.status === 'completed') { active.outbox.add(reply); this.flush(active); }
      else {
        this.send(generateMacroAnalysisMessage({ type: MacroAnalysisMessageType.PROGRESS, requestId: reply.requestId, reply: { ...reply, lease: active.lease } }));
        if (reply.status === 'failed' || reply.status === 'cancelled') this.stopActive('analysis_native_' + reply.status, true);
      }
    } catch { this.fail('analysis_invalid_native_evidence'); }
  }
  private async advertise(): Promise<void> {
    if (this.probing) return;
    this.probing = true; this.probeAt = this.clock.now();
    try {
      const capabilities = await this.native.capabilities();
      if (this.authenticated && this.realtime.isConnected()) this.send(generateMacroAnalysisMessage({ type: MacroAnalysisMessageType.CAPABILITIES,
        requestId: 'analysis-capabilities', payload: { kind: 'analysis', requestId: 'analysis-capabilities', status: 'capabilities', inputsSent: false, capabilities } }));
    } catch { this.log('analysis_host_unavailable', { errorCode: 'native_capabilities_unavailable' }); }
    finally { this.probing = false; }
  }
  private flush(active: ActiveAnalysis): void {
    for (const message of active.outbox.messages(this.clock.now(), active.lease)) this.send(message);
  }
  private fail(reason: string): void {
    const active = this.active;
    const parsed = analysisProgressSchema.safeParse({ jobId: 'probe', manifestDigest: 'a'.repeat(64), status: 'failed',
      target: { campaignId: 'probe', trigger: 'launch', variant: 'draft', rev: 0 }, expectedBindings: 0, completedBindings: 0,
      casesDone: 0, casesTotal: 0, errorCode: reason });
    if (active) this.send(generateMacroAnalysisMessage({ type: MacroAnalysisMessageType.PROGRESS, requestId: active.requestId,
      reply: { kind: 'analysis', requestId: active.requestId, inputsSent: false, status: 'failed', lease: active.lease,
        sequence: active.lastSequence + 1, ...(parsed.success && parsed.data.errorCode ? { errorCode: parsed.data.errorCode } : {}) } }));
    this.stopActive(reason, true);
  }
  private stopActive(reason: string, promotePreview: boolean): void {
    const active = this.active; this.active = undefined;
    if (active) {
      void this.native.send({ command: 'analysisCancel', requestId: active.requestId, lease: active.lease })
        .catch(() => this.log('analysis_cancel_delivery_failed', { jobId: active.lease.jobId, epoch: active.lease.epoch }));
      this.log('analysis_host_stopped', { jobId: active.lease.jobId, epoch: active.lease.epoch, reason });
    }
    const queued = promotePreview && active?.kind === 'calibration' ? this.queuedPreview : undefined;
    if (queued) { this.queuedPreview = undefined; void this.acceptAssignment(queued); }
    else this.publishSurface();
  }
  private publishSurface(): void {
    this.native.noteAssignment(this.active?.kind ?? 'idle', !!this.queuedPreview);
  }
  private sameAttempt(left: Lease, right: Lease): boolean {
    return left.jobId === right.jobId && left.epoch === right.epoch && left.hostId === right.hostId && left.socketId === right.socketId
      && left.manifestDigest === right.manifestDigest && left.workPlanDigest === right.workPlanDigest;
  }
  private send(message: MacroAnalysisMessage): void {
    try { if (this.authenticated && this.realtime.isConnected()) this.realtime.sendMessage(message); }
    catch { this.log('analysis_socket_delivery_failed', { type: message.type }); }
  }
  private log(name: string, fields: Record<string, string | number | boolean>): void {
    try { this.logger.event(name, fields); } catch { /* Diagnostics cannot block cancellation. */ }
  }
}
